// The Wingman chat on the Quoting page (Lane 1376 P1, plan section 3): the same
// chat as the sourcing event and the Meeting Prep deck (shared frame, dock, bubble
// and state), with a transport for one RFQ. Answers only: no cards, no actions.
//
// Each RFQ has its own conversation: the panel is keyed by the RFQ, so clicking
// another RFQ shows that RFQ's conversation (an answer still being written for the
// first one is saved there, and shows when the quoter goes back). The first line is
// written by the server, never by the model: the RFQ and one question.
//
// The server decides whether the chat exists here (flag, Quoting, Mirror chat):
// anything but an available chat shows no bubble and no dock. A load that failed
// (the network, a server error) is not "unavailable": the bubble stays and the
// panel offers Try again.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useWorkspaceChat from '../../components/workspace-chat/useWorkspaceChat';
import useDockedChat from '../../components/workspace-chat/useDockedChat';
import { ChatDock } from '../../components/workspace-chat/ChatDock';
import WorkspaceChatPanel from '../../components/workspace-chat/WorkspaceChatPanel';
import ChatBubble from '../../components/workspace-chat/ChatBubble';
import focusChat from '../../components/workspace-chat/focusChat';

const CHAT_DOCK_ID = 'qtng-chat-dock';
const CHAT_STORAGE_PREFIX = 'qtng-chat';

function QuotingChatPanel({ api, rfqId, onAvailable, onClose, onRetry }) {
  const [opening, setOpening] = useState(null);
  const [label, setLabel] = useState(null);
  const [failed, setFailed] = useState(false);
  const transport = useMemo(() => ({
    // 404: no chat here (switch off, Quoting off, not this company's RFQ).
    load: () => api.chat(rfqId).then((r) => {
      setOpening((r && r.opening) || null);
      setLabel((r && r.label) || null);
      setFailed(false);
      return r;
    }).catch((e) => {
      if (e && e.status === 404) return { available: false };
      setFailed(true);
      throw e;
    }),
    create: () => api.newConversation(rfqId),
    send: (body, onEvent) => api.chatTurn(rfqId, body, onEvent),
  }), [api, rfqId]);
  const chat = useWorkspaceChat({ transport, chatSurface: 'quoting' });
  useEffect(() => { onAvailable(failed ? 'failed' : chat.available); },
    [failed, chat.available, onAvailable]);
  return (
    <WorkspaceChatPanel
      chat={chat}
      title="Wingman chat"
      ariaLabel="Chat about this RFQ"
      unavailableText="Chat is not available here."
      emptyState={null}
      placeholder="Ask about this RFQ or anything else…"
      contextChip={failed ? (
        <div className="wchat-context">
          The chat could not load.
          <button type="button" className="wchat-clr" onClick={onRetry}>Try again</button>
        </div>
      ) : label ? (
        <div className="wchat-context"><span className="wchat-chip" title={label}>About {label}</span></div>
      ) : null}
      topNote={opening ? (
        <div className="wchat-turn wchat-opening" data-testid="qtng-chat-opening">
          {/* Plain text: it carries the customer's name as written. */}
          <div>{opening}</div>
        </div>
      ) : null}
      onClose={onClose}
    />
  );
}

export default function QuotingChat({ api, rfqId }) {
  const dock = useDockedChat({ storagePrefix: CHAT_STORAGE_PREFIX, defaultOpen: false });
  const [available, setAvailable] = useState(null);
  const [attempt, setAttempt] = useState(0);   // Try again remounts the panel
  const shown = available === true || available === 'failed';
  // Lane 1390: the bubble shows only while the chat is closed, so opening moves focus to
  // the message box and closing brings it back to the bubble (as on the deck).
  const bubbleRef = useRef(null);
  const moveFocus = useRef(null);
  const setOpen = (next) => {
    moveFocus.current = next ? 'input' : 'bubble';
    dock.setOpen(next);
  };
  useEffect(() => {
    const target = moveFocus.current;
    moveFocus.current = null;
    if (target === 'input' && dock.open) {
      focusChat(document.getElementById(CHAT_DOCK_ID));
    } else if (target === 'bubble' && !dock.open && bubbleRef.current) {
      bubbleRef.current.focus();
    }
  }, [dock.open]);
  if (!rfqId) return null;
  return (
    <>
      {shown && <ChatBubble ref={bubbleRef} open={dock.open} onToggle={() => setOpen(true)}
        controls={CHAT_DOCK_ID} />}
      <ChatDock dock={{ ...dock, open: dock.open && shown }} id={CHAT_DOCK_ID}>
        <QuotingChatPanel key={`${rfqId}:${attempt}`} api={api} rfqId={rfqId} onAvailable={setAvailable}
          onClose={() => setOpen(false)} onRetry={() => setAttempt((n) => n + 1)} />
      </ChatDock>
    </>
  );
}
